/**
 * defensibility.ts - Central registry of the evidence, assumptions, limits,
 *                    and reproducibility metadata that make every claim in
 *                    the platform defensible.
 *
 *   - REGULATORY_THRESHOLDS  : every DWI / WFD / WHO / Ofwat limit cited
 *   - MODEL_ASSUMPTIONS      : explicit assumptions baked into each module
 *   - CALIBRATION_BOUNDS     : where each model is validated vs extrapolated
 *   - PLATFORM_LIMITATIONS   : honest list of what the tool does NOT do
 *   - VERIFICATION_STATUS    : internal vs external peer-review status
 *   - REPRODUCIBILITY        : version / build / RNG / dependency metadata
 *   - CONFLICT_DISCLOSURES   : authorship / funding / commercial interests
 */

export interface RegulatoryThreshold {
  parameter: string;
  threshold: string;
  regulation: string;
  jurisdiction: 'UK' | 'EU' | 'US' | 'WHO' | 'Industry';
  legalForce: 'statutory PCV' | 'guideline' | 'standard' | 'industry benchmark';
  notes: string;
}

export const REGULATORY_THRESHOLDS: RegulatoryThreshold[] = [
  {
    parameter: 'Manganese',
    threshold: '50 -g/L',
    regulation: 'Water Supply (Water Quality) Regulations 2016, SI 614, Sch 1 - Drinking Water Inspectorate PCV',
    jurisdiction: 'UK',
    legalForce: 'statutory PCV',
    notes: 'Operates from 27 June 2016. Tier 1 substance - supply non-compliance triggers DWI Reg 28 notification.',
  },
  {
    parameter: 'Iron',
    threshold: '200 -g/L',
    regulation: 'Water Supply (Water Quality) Regulations 2016, SI 614, Sch 1',
    jurisdiction: 'UK',
    legalForce: 'statutory PCV',
    notes: 'Aesthetic / acceptability parameter.',
  },
  {
    parameter: 'Geosmin / 2-MIB',
    threshold: '10 ng/L (sensory)',
    regulation: 'DWI customer-acceptable threshold; UKWIR Report 06/DW/02/49 (2007)',
    jurisdiction: 'UK',
    legalForce: 'guideline',
    notes: 'Not a statutory PCV but failing this drives customer complaints and Reg 4(2)(a) "aesthetic acceptability" obligation.',
  },
  {
    parameter: 'Chlorophyll-a (good ecological status)',
    threshold: '< 8 -g/L low-alkalinity / < 18 -g/L high-alkalinity (annual mean)',
    regulation: 'Water Framework Directive 2000/60/EC; UK TAG Phytoplankton Boundary Values 2014',
    jurisdiction: 'EU',
    legalForce: 'guideline',
    notes: 'WFD Phyto-PIC reference boundaries. Above values trigger "moderate" - action required.',
  },
  {
    parameter: 'Total Phosphorus (good ecological status)',
    threshold: '< 20-40 -g/L (lake-type dependent)',
    regulation: 'UK TAG TP Boundary Values 2008 (rev 2014); WFD',
    jurisdiction: 'EU',
    legalForce: 'guideline',
    notes: 'Type-specific thresholds - Barcombe-class shallow polymictic is LM (low alkalinity / moderate altitude).',
  },
  {
    parameter: 'Dissolved oxygen at sediment-water interface',
    threshold: '> 4 mg/L sustained',
    regulation: 'Engineering best-practice - Cooke et al. (2005) Restoration & Management of Lakes & Reservoirs, 3rd ed., CRC Press',
    jurisdiction: 'Industry',
    legalForce: 'industry benchmark',
    notes: 'Threshold below which Fe(III)-Fe(II) reduction and P release accelerate (Mortimer 1942, validated in Sempach, Hallwil, Bautzen).',
  },
  {
    parameter: 'Sediment ORP for P-stable bed',
    threshold: '> + 250 mV (Eh)',
    regulation: 'Davison (1993) Earth-Science Reviews 34: 119-163',
    jurisdiction: 'Industry',
    legalForce: 'industry benchmark',
    notes: 'ORP at which Fe(II) oxidation kinetics exceed Fe(III) reduction at typical lake pH and DO.',
  },
  {
    parameter: 'Microcystin (drinking-water)',
    threshold: '1.0 -g/L',
    regulation: 'WHO Guidelines for Drinking-Water Quality (4th ed., 2017) - provisional value for total MC-LR equivalents',
    jurisdiction: 'WHO',
    legalForce: 'guideline',
    notes: 'Operational threshold used by UK WTW; UK has no statutory PCV but DWI expects monitoring during cyanobacteria events.',
  },
  {
    parameter: 'OTE for fine-bubble destratification',
    threshold: '> 25 % at design depth',
    regulation: 'Stenstrom (2008) WEF Aeration Manual; ASCE Standard 2-06 (2007) Clean-water test',
    jurisdiction: 'Industry',
    legalForce: 'standard',
    notes: 'Below this threshold the WLC of the scheme typically fails the -1.5 / kg-O- benchmark.',
  },
  {
    parameter: 'WLC discount rate',
    threshold: '3.5 % (real)',
    regulation: 'HM Treasury Green Book (2022 ed., Annex A6)',
    jurisdiction: 'UK',
    legalForce: 'standard',
    notes: 'Mandatory for public-sector water-industry option appraisal under AMP-8.',
  },
];

export interface ModelAssumption {
  module: string;
  assumption: string;
  validityRegime: string;
  impactIfViolated: string;
}

export const MODEL_ASSUMPTIONS: ModelAssumption[] = [
  {
    module: 'asaeda_c',
    assumption: 'Steady-state plume rise; round-tube bubble source; ambient stratification N constant over plume rise',
    validityRegime: 'Shallow reservoirs (depth < 15 m) with continuous diffuser operation',
    impactIfViolated: 'C-criterion under-predicts merging for transient (cycled) sources; over-predicts for highly stratified deep water - use W-est module instead',
  },
  {
    module: 'wuest_double_plume',
    assumption: 'Two-phase inner/outer plume coupling; ambient N(z) profile linearised over rise height',
    validityRegime: 'Deep stratified water (> 15 m); N > 0.005 s--; bubble plumes that do not reach the surface',
    impactIfViolated: 'Detrainment height predictions -15-30 % in transitional regimes - switch to Asaeda when plume reaches surface',
  },
  {
    module: 'mehta_partheniades',
    assumption: 'Cohesive bed with uniform critical-shear stress -_ce; Soulsby quadratic-drag formulation',
    validityRegime: 'Mud / clay-rich beds (>10 % fines); near-bed flow < 0.5 m/s',
    impactIfViolated: 'Cohesionless / sand beds use Shields entrainment instead; high-velocity zones near diffusers (>0.5 m/s) need direct CFD',
  },
  {
    module: 'boudreau_diagenesis',
    assumption: 'Steady-state porewater; sedimentation rate << diffusion rate; uniform porosity over top 10 cm',
    validityRegime: 'Lake & freshwater reservoir beds; 0 < z < 10 cm sediment depth',
    impactIfViolated: 'Active sedimentation zones (e.g. inflow deltas) under-predict; combine with Aller bioturbation when macrofauna present',
  },
  {
    module: 'kumagai_feedback',
    assumption: 'Cyanobacteria are the dominant chl-a contributor (>30 %); surface 0.5 m sub-layer absorbs incoming shortwave',
    validityRegime: 'Eutrophic surface waters; chl-a 5-80 -g/L; stratified periods only',
    impactIfViolated: 'Diatom-dominated systems under-predict surface heating; reset A = 1 if f_cyano < 0.25',
  },
  {
    module: 'davison_fe_mn',
    assumption: 'Pseudo-first-order oxidation kinetics; trace-metal speciation dominated by Fe(II)/Mn(II); abiotic oxidation pathway',
    validityRegime: 'pH 5-9; DO 0.1-12 mg/L; bicarbonate alkalinity > 30 mg/L',
    impactIfViolated: 'Biological Mn oxidation by Leptothrix can be 100- faster than abiotic - site-specific microbiology data required',
  },
  {
    module: 'mcginnis_bubble',
    assumption: 'Single rising bubble in quiescent water; Fr-ssling mass-transfer coefficient; ideal-gas EoS',
    validityRegime: 'Bubble diameter 0.5-15 mm; depth 1-100 m; ambient water at near-saturation pressure',
    impactIfViolated: 'Plume bubble swarms have coalescence/break-up dynamics not captured - combine with coalescence module',
  },
  {
    module: 'geosmin_predictor',
    assumption: 'Wang 2010 multivariate regression coefficients valid for Chinese reservoirs; transferable to UK polymictic systems',
    validityRegime: 'Chl-a 2-100 -g/L; cyanobacteria fraction 0-100 %; T 8-30 -C; eutrophic reservoirs',
    impactIfViolated: 'Empirical regression - out-of-range extrapolations carry -50 % uncertainty; calibrate locally where possible',
  },
  {
    module: 'redox_p_release',
    assumption: 'Sigmoidal release function (Ma & Wu 2008) fitted on Taihu sediments; Fe-bound P pool dominant',
    validityRegime: 'ORP -300 to +400 mV; Fe-rich sediments; pH 6.5-8.5',
    impactIfViolated: 'Al-bound / Ca-bound P sediments follow different kinetics; calcite-buffered alkaline systems need separate model',
  },
  {
    module: 'nom_sod',
    assumption: 'Linear NOM enhancement capped at 3- base SOD; Arrhenius theta = 1.07 (Mueller-Burris-Boyle 2002)',
    validityRegime: 'NOM (DOC) 2-25 mg/L; T 8-25 -C; aerobic top sediment',
    impactIfViolated: 'Above 25 mg/L NOM the relationship plateaus further - cap prevents runaway feedback',
  },
];

export interface CalibrationBound {
  module: string;
  variable: string;
  calibratedRange: string;
  barcombeValue: string;
  inRange: boolean;
}

export const CALIBRATION_BOUNDS: CalibrationBound[] = [
  { module: 'asaeda_c', variable: 'N (Brunt-V-is-l-)',
    calibratedRange: '0.005 - 0.04 s--', barcombeValue: '~ 0.012 s--', inRange: true },
  { module: 'kumagai_feedback', variable: 'Chl-a',
    calibratedRange: '5 - 80 -g/L', barcombeValue: '30 -g/L (mean), 70 -g/L (peak)', inRange: true },
  { module: 'np_colimitation', variable: 'TN / TP',
    calibratedRange: 'TN 0.1-5 mg/L; TP 0.005-0.5 mg/L', barcombeValue: 'TN 1.2 / TP 0.08 mg/L', inRange: true },
  { module: 'davison_fe_mn', variable: 'pH',
    calibratedRange: '5 - 9', barcombeValue: '7.6', inRange: true },
  { module: 'mcginnis_bubble', variable: 'Bubble diameter',
    calibratedRange: '0.5 - 15 mm', barcombeValue: '2.0 mm (live spec)', inRange: true },
  { module: 'wuest_double_plume', variable: 'Depth',
    calibratedRange: '15 - 90 m', barcombeValue: '2.72 m (mean, J2020 survey)',
    inRange: false /* W-est is outside Barcombe range - flagged */ },
  { module: 'mehta_partheniades', variable: '-_ce',
    calibratedRange: '0.05 - 1.0 Pa', barcombeValue: '~ 0.15 Pa typical', inRange: true },
  { module: 'boudreau_diagenesis', variable: 'Sediment depth',
    calibratedRange: '0 - 10 cm', barcombeValue: 'top 5 cm modelled', inRange: true },
  { module: 'geosmin_predictor', variable: 'Site latitude',
    calibratedRange: 'Chinese subtropical reservoirs (Wang 2010)', barcombeValue: 'SE England temperate',
    inRange: false /* Empirical regression cross-applied - flagged */ },
  { module: 'nom_sod', variable: 'NOM (DOC)',
    calibratedRange: '2 - 25 mg/L', barcombeValue: '8 mg/L', inRange: true },
];

export interface PlatformLimitation {
  area: string;
  limitation: string;
  workaround: string;
}

export const PLATFORM_LIMITATIONS: PlatformLimitation[] = [
  {
    area: 'Spatial resolution',
    limitation: 'Parametric bathymetry (polygon + radial depth) not a true 3-D DEM. Plume-coverage map is a 2-D projection, not a 3-D Lagrangian particle track.',
    workaround: 'For final design verification use ELCOM/CAEDYM 3-D run (ELCOM coupling module). The in-app tool is for scoping & relative comparison, not as-built sizing.',
  },
  {
    area: 'Time resolution',
    limitation: 'Algal Dynamics and Thermal Model run on daily time-step. Sub-daily events (e.g. afternoon convective storm causing partial mixing) are smoothed.',
    workaround: 'Provide hourly met forcing if available; otherwise document the smoothing as a known limitation in commissioning reports.',
  },
  {
    area: 'Phytoplankton resolution',
    limitation: 'CSR functional-group model (C / S / R) is a 3-class abstraction. Real reservoirs contain dozens of species with site-specific responses.',
    workaround: 'Use as a qualitative succession indicator only; ground-truth with periodic species-level laboratory counts.',
  },
  {
    area: 'Biological feedbacks',
    limitation: 'Macrofaunal recolonisation after aeration restart is modelled as linear in DO; real ecological succession can take 6-24 months and is non-linear.',
    workaround: 'Treat Aller biodiffusion enhancement as an upper bound; phase aeration capacity assuming 12-month colonisation lag.',
  },
  {
    area: 'Climate-change projection',
    limitation: 'Default temperature & wind forcing reflect 1990-2020 baselines. UKCP18 projections (+2 -C by 2050) are NOT automatically applied.',
    workaround: 'Run sensitivity scenarios manually with elevated surface T and weakened wind; document in DWI 5-yr submission.',
  },
  {
    area: 'Inflow / outflow dynamics',
    limitation: 'Constant residence-time assumption. River inflow plumes, stormwater pulses and abstraction draw-down patterns are not resolved.',
    workaround: 'Where flow-through is dominant (e.g. M-ggelsee-style continuous Spree exchange) use a CSTR mass balance externally.',
  },
  {
    area: 'Empirical-model transferability',
    limitation: 'Some modules (Kumagai 2000 Lake Biwa; Wang 2010 Chinese reservoirs; Ma & Wu 2008 Taihu) are calibrated on Asian eutrophic lakes. Direct application to UK reservoirs is approximate.',
    workaround: 'Treat empirical coefficients as initial estimates; calibrate locally during Year-1 commissioning monitoring.',
  },
];

export interface VerificationItem {
  scope: string;
  status: 'self-verified' | 'peer-reviewed source' | 'independent test' | 'planned';
  evidence: string;
}

export const VERIFICATION_STATUS: VerificationItem[] = [
  { scope: 'Module physics equations', status: 'peer-reviewed source',
    evidence: 'Every module formula traced to a primary peer-reviewed source (visible in ModuleInfoModal popups).' },
  { scope: 'Benchmark site data', status: 'peer-reviewed source',
    evidence: 'UKCEH NERC EIDC (Loch Leven), IGB LTER (M-ggelsee), FBA / J. Talling (Esthwaite), H-lsmann 2005 (Bautzen).' },
  { scope: 'Regulatory thresholds', status: 'peer-reviewed source',
    evidence: 'DWI 2016 Regulations, WFD 2000/60/EC, HM Treasury Green Book - all primary statutory / standard texts.' },
  { scope: 'Cost model coefficients', status: 'peer-reviewed source',
    evidence: 'Stenstrom 2008 WEF; Ofwat PR19 ASR benchmark; OEE-published electricity unit costs.' },
  { scope: 'Combined train output (predicted vs observed)', status: 'self-verified',
    evidence: 'Internal cross-check: TrainSummaryPage Barcombe defaults reproduce Loch Leven recovery trajectory within -25 %.' },
  { scope: 'Engineering peer review', status: 'planned',
    evidence: 'Recommendation: chartered CWEM / CEnv review of the integrated framework before AMP-8 submission.' },
  { scope: 'External SQEP (Suitably Qualified Experienced Person) audit', status: 'planned',
    evidence: 'Recommendation: ICE / CIWEM SQEP sign-off prior to use in any regulatory submission.' },
];

export interface ReproMetadata {
  /** Platform / build identifier displayed on every PDF. */
  version: string;
  /** Date this build was produced. */
  buildDate: string;
  /** Random-number generator seeds for stochastic modules (NSGA-II, DDS). */
  rngStrategy: string;
  /** Dependency versions sampled at build. */
  deps: { name: string; version: string }[];
}

export const REPRODUCIBILITY: ReproMetadata = {
  version: 'v74 (May 2026)',
  buildDate: '2026-05-21',
  rngStrategy: 'NSGA-II and DDS optimisers use seeded Mersenne Twister; seed printed in PDF appendix for full reproducibility.',
  deps: [
    { name: 'React',          version: '18.x' },
    { name: 'Vite',           version: '7.3' },
    { name: 'jsPDF',          version: '2.5' },
    { name: 'jspdf-autotable', version: '3.x' },
    { name: 'Lucide-React',   version: 'latest' },
  ],
};

export interface ConflictDisclosure {
  party: string;
  disclosure: string;
}

export const CONFLICT_DISCLOSURES: ConflictDisclosure[] = [
  {
    party: 'Reynolds & Bauhm Ltd',
    disclosure: 'Developer and IP owner of the simulation framework. Holds no manufacturing or supply relationships with any diffuser, blower or LOX-equipment vendor mentioned in the comparison tables.',
  },
  {
    party: 'Authoring engineers',
    disclosure: 'No paid advisory roles with any party named in the recommended train templates. No undeclared shareholdings in companies whose products are referenced in cost benchmarks.',
  },
  {
    party: 'Commissioning client',
    disclosure: 'Where this tool is used in support of a paid commission (e.g. South East Water / Atkins for Barcombe), the commercial relationship is declared on the report cover page.',
  },
];

/**
 * One-line headline summary for use in PDF executive sections.
 */
export const DEFENSIBILITY_HEADLINE =
  'Every numerical claim is traceable to a primary peer-reviewed source, ' +
  'regulatory text, or industry standard. Empirical coefficients carry ' +
  'explicit calibration bounds; where Barcombe is outside the original ' +
  'calibration range, the affected module is flagged in Section ' +
  'Calibration vs Barcombe.';
